/**
 * Consolidated interfaces for profile-related components
 * Contains all interfaces and types used across the profile management system
 */

export namespace NProfiles {
    // ===== Core Profile Data Interfaces =====

    export interface IMemberProfile {
        name?: string; // Only for family members
        location?: string; // Country code for location
        favouriteColours: {
            [key: string]: boolean;
        };
        relationship?: string; // Only for family members
        dateOfBirth?: string; // Only for family members
        sizeGroup?: ESizeGroup; // Selected size group (women, men, children)
        sizes?: { [key: string]: number[] }; // Size selections by type title (multiple per type)
        selectedSizeGroup?: ESizeGroup; // Selected size group (women, men, children)
        buyFor: {
            myself?: boolean;
            myselfHorse?: boolean;
            children?: boolean;
            childrenHorse?: boolean;
            friend?: boolean;
            friendHorse?: boolean;
        }; // Only user's profile
        compete?: ECompeteOptions; // Only user's profile
        disciplines: {
            [key: string]: boolean;
        };
        isRiding?: boolean;
        isHorseOwner?: boolean;
        otherDiscipline?: string; // For custom discipline input
        styleOutfits?: string; // For custom style outfits input
    }

    export const defaultMemberProfile: IMemberProfile = {
        favouriteColours: {},
        disciplines: {},
        buyFor: {}
    };

    export const defaultHorseProfile: IHorseProfile = {
        sizes: {}
    };

    export interface IHorseProfile {
        name?: string;
        height?: string;
        colour?: number; // Keep for backward compatibility
        breeding?: string;
        dateOfBirth?: string; // Date of birth in yyyy-MM-dd format
        sizes: { [key: string]: number[] }; // Size selections
    }

    export interface IProfileData {
        customer?: IMemberProfile;
        horses?: IHorseProfile[];
        familyMembers?: IMemberProfile[];
    }

    // ===== Enums =====

    export enum ECompeteOptions {
        regular = 'regular',
        occasional = 'occasional',
        no = 'no'
    }

    export enum ESizeGroup {
        children = 'children',
        women = 'women',
        men = 'men'
    }

    // ===== Size Selector Interfaces =====

    export interface ISizeGroup {
        group: ESizeGroup;
        types: ISizeType[];
    }

    export interface ISizeType {
        title: string;
        options: number[];
    }

    // ===== Color Selector Interfaces =====

    export interface IAttributeOption {
        attributeOptionId: number;
    }

    // ===== CMS Content Interfaces =====

    export interface IAccountProfilesContent {
        antavoMissionId?: string;
    }

    export interface IMemberEditContent {
        sizeGroups: ISizeGroup[];
        disciplineOptions: IOption[];
        competeOptions: IOption[];
        favouriteColourOptions: IAttributeOption[];
        relationshipOptions: IOption[];
    }

    export interface IHorseEditContent {
        // horseColourOptions: IAttributeOption[];
        breedingOptions: IBreedingOption[];
        horseSizeTypes: ISizeType[];
    }

    // ===== Option Interfaces =====

    export interface IOption {
        value: string;
        label: string;
    }

    export interface IColourOption {
        value: string;
        label: string;
        image?: string;
    }

    export interface IBreedingOption {
        value: string;
        label: string;
    }

    // ===== Component IDs =====

    export const COMPONENT_IDS = {
        accountProfiles: 'account-profiles',
        memberEdit: 'member-edit',
        horseEdit: 'horse-edit'
    } as const;

    export const COLOUR_ATTRIBUTE_CODE = 'colour_filter';
}
